import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useElements, useStripe, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ErrorMessage, LoadingSpinner } from '../../../components/common';
import Button from '../../../components/ui/Button';
import { TruckIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

// Import extracted components
import FinancialSummary from '../../../components/checkout/components/FinancialSummary';
import CancelGroupReturnModal from '../../../components/actions/shared/modals/CancelGroupReturnModal';
import ReturnShippingForm from '../../../components/checkout/components/ReturnShippingForm';
import ReturnItemsList from '../../../components/checkout/components/ReturnItemsList';
import ReturnActionButtons from '../../../components/checkout/components/ReturnActionButtons';

// Import custom hooks
import { useShippingRateCalculator } from '../../../hooks/useShippingRateCalculator';
import { useReturnItemsProcessor } from '../../../hooks/useReturnItemsProcessor';
import { useReturnAddresses } from '../../../hooks/useReturnAddresses';

// Redux API hooks
import { 
  useGetSavedPaymentMethodsQuery
} from '../../../redux/apis/endpoints/paymentApi';
import { useGetFulfillmentGroupDetailsQuery } from '../../../redux/apis/endpoints/orderApi';
import { 
  useCancelFulfillmentGroupMutation
} from '../../../redux/apis/endpoints/fulfillmentGroupApi';
import { useGetAddressesQuery } from '../../../redux/apis/endpoints/addressApi';
import { useCreateReturnShippingLabelMutation } from '../../../redux/apis/endpoints/deliveryApi';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Wrapper component that provides Stripe elements
const ReturnShippingPageWrapper = () => {
  return (
    <Elements stripe={stripePromise}>
      <ReturnShippingPage />
    </Elements>
  );
};

const ReturnShippingPage = () => {
  const { fulfillmentGroupId } = useParams();
  const history = useHistory();
  
  const [selectedRate, setSelectedRate] = useState(null);
  const [isGeneratingLabel, setIsGeneratingLabel] = useState(false);
  const [error, setError] = useState(null);
  const [parcels, setParcels] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [displayableRates, setDisplayableRates] = useState([]);
  const [rawRates, setRawRates] = useState([]);

  // RTK Query hooks
  const { 
    data: fgData, 
    isLoading: isLoadingFG,
    error: fgError,
    refetch: refetchFulfillmentGroup
  } = useGetFulfillmentGroupDetailsQuery(fulfillmentGroupId);

  const { 
    data: customerAddresses = [], 
    isLoading: addressesLoading,
  } = useGetAddressesQuery(undefined, {
    skip: !useSelector(state => state.auth?.user)
  });

  const { 
    data: paymentMethods,
    isLoading: isLoadingPaymentMethods
  } = useGetSavedPaymentMethodsQuery();

  const [createReturnShippingLabel, { isLoading: isGeneratingReturnLabel }] = useCreateReturnShippingLabelMutation();
  const [cancelFulfillmentGroup, { isLoading: isCancellingReturn }] = useCancelFulfillmentGroupMutation();

  // Use custom hooks for complex logic
  const {
    approvedReturnItems,
    setApprovedReturnItems,
    groupReturnReason,
    allHaveLabels,
    existingLabelData
  } = useReturnItemsProcessor(fgData);

  const {
    selectedCustomerAddress,
    setSelectedCustomerAddress,
    selectedMerchantAddress,
    shouldCalculateRates,
    setShouldCalculateRates,
    returnDetails
  } = useReturnAddresses(fgData, approvedReturnItems, customerAddresses, allHaveLabels);

  // Extract parcel information
  useEffect(() => {
    if (approvedReturnItems?.length > 0) {
      try {
        const extractedParcels = approvedReturnItems.map(item => {
          const parcelInfo = item?.parcelInfo || item?.product?.parcelInfo;
          if (!parcelInfo) {
            console.warn(`Missing parcel info for item ${item.id}`);
            return null;
          }
          
          return {
            weight: String(parcelInfo.weight || '0.5'),
            mass_unit: parcelInfo.units?.mass || 'lb',
            length: String(parcelInfo.length || '10'),
            width: String(parcelInfo.width || '6'),
            height: String(parcelInfo.height || '0.25'),
            distance_unit: parcelInfo.units?.distance || 'in'
          };
        }).filter(Boolean);
        
        if (extractedParcels.length > 0) {
          setParcels(extractedParcels);
        }
      } catch (error) {
        console.error('Error extracting parcel information:', error);
      }
    }
  }, [approvedReturnItems]);

  // Shipping rate calculator callbacks
  const handleCalculationStart = useCallback(() => setError(null), []);
  const handleRatesSuccess = useCallback((displayRates, originalRates) => {
    setDisplayableRates(displayRates);
    setRawRates(originalRates);
    if (displayRates.length > 0) {
      const cheapestRate = displayRates.sort((a, b) => a.price - b.price)[0];
      setSelectedRate(cheapestRate);
    }
  }, []);
  const handleRatesUnshippable = useCallback((unshippableItemIds, message) => {
    setError(message || 'These items cannot be shipped from this address.');
    setDisplayableRates([]);
    setRawRates([]);
  }, []);
  const handleRatesError = useCallback((message) => {
    setError(message || 'Unable to calculate shipping rates. Please try again.');
    setDisplayableRates([]);
    setRawRates([]);
  }, []);

  // Check if we have existing return deliveries with shipping rates
  const hasExistingShippingRate = useMemo(() => {
    return fgData?.returnDeliveries?.length > 0 && 
           fgData.returnDeliveries.some(delivery => delivery.shipping_rate > 0);
  }, [fgData?.returnDeliveries]);

  // Check if FG status indicates labels already created
  const labelsAlreadyCreated = useMemo(() => {
    const isShipped = fgData?.status === 'return_shipped';
    return isShipped;
  }, [fgData?.status]);

  // Use shipping rate calculator
  const { isLoading: isLoadingRates } = useShippingRateCalculator({
    customerAddress: shouldCalculateRates && !allHaveLabels && !hasExistingShippingRate && !labelsAlreadyCreated ? selectedCustomerAddress : null, 
    merchantAddress: shouldCalculateRates && !allHaveLabels && !hasExistingShippingRate && !labelsAlreadyCreated ? selectedMerchantAddress : null,
    items: !allHaveLabels && !hasExistingShippingRate && !labelsAlreadyCreated ? approvedReturnItems : [],
    addressId: 'return',
    currency: approvedReturnItems[0]?.currency || 'USD',
    onCalculationStart: handleCalculationStart,
    onRatesSuccess: handleRatesSuccess,
    onRatesUnshippable: handleRatesUnshippable,
    onRatesError: handleRatesError,
    isReturn: true
  });

  // Calculate costs
  const shippingCost = useMemo(() => {
    // If there are existing return deliveries, use their shipping rates regardless of allHaveLabels status
    if (fgData?.returnDeliveries?.length > 0) {
      const totalDeliveryShippingCost = fgData.returnDeliveries.reduce((sum, delivery) => {
        return sum + (parseFloat(delivery.shipping_rate) || 0);
      }, 0);
      return totalDeliveryShippingCost;
    }
    
    // Otherwise, use the selected rate during rate selection
    return selectedRate ? parseFloat(selectedRate.price) || 0 : 0;
  }, [selectedRate, fgData?.returnDeliveries]);

  const requiresPayment = useMemo(() => {
    if (!approvedReturnItems || approvedReturnItems.length === 0) return false;
    
    const totalProductPrice = approvedReturnItems.reduce((sum, item) => {
      return sum + (parseFloat(item.price || item.amount) || 0) * (item.quantity || 1);
    }, 0);
    
    const totalUsageFee = approvedReturnItems.reduce((sum, item) => {
      return sum + (parseFloat(item.returnRequest?.usageFee) || 0);
    }, 0);
    
    return totalUsageFee + shippingCost > totalProductPrice;
  }, [approvedReturnItems, shippingCost]);
  
  const paymentAmount = useMemo(() => {
    if (!approvedReturnItems || approvedReturnItems.length === 0) return 0;
    
    const totalProductPrice = approvedReturnItems.reduce((sum, item) => {
      return sum + (parseFloat(item.price || item.amount) || 0) * (item.quantity || 1);
    }, 0);
    
    const totalUsageFee = approvedReturnItems.reduce((sum, item) => {
      return sum + (parseFloat(item.returnRequest?.usageFee) || 0);
    }, 0);
    
    return totalUsageFee + shippingCost - totalProductPrice;
  }, [approvedReturnItems, shippingCost]);

  // Event handlers
  const handleRateSelect = (rate) => setSelectedRate(rate);

  const handleCustomerAddressChange = (e) => {
    if (!e || !e.target) return;
    
    const selectedAddressId = e.target.value;
    if (selectedAddressId === 'new') {
      history.push('/dashboard/settings/addresses/new?returnUrl=' + encodeURIComponent(window.location.pathname));
      return;
    }
    
    if (error) setError(null);
    
    const newSelectedAddress = customerAddresses.find(addr => addr.id === selectedAddressId);
    if (newSelectedAddress) {
      setSelectedCustomerAddress(newSelectedAddress);
      if (selectedMerchantAddress && !allHaveLabels) {
        setShouldCalculateRates(true);
      }
    }
  };

  const handlePaymentSuccess = async (result) => {
    try {
      // Early exit if already generating a label or if labels already exist
      if (isGeneratingLabel || allHaveLabels) {
        return;
      }

      setIsGeneratingLabel(true);
      
      if (!selectedRate) {
        throw new Error('Please select a shipping rate first');
      }
      
      const labelResponse = await createReturnShippingLabel({
        fulfillmentGroupId,
        fromAddressId: selectedCustomerAddress?.id,
        toAddressId: selectedMerchantAddress?.id && !selectedMerchantAddress.id.toString().startsWith('merchant-')
          ? selectedMerchantAddress.id 
          : undefined,
        toAddress: selectedMerchantAddress?.id.toString().startsWith('merchant-')
          ? selectedMerchantAddress 
          : undefined,
        ...(parcels && { parcels }),
        rateId: selectedRate.id,
        paymentIntentId: result?.paymentIntentId,
        isReturn: true
      }).unwrap();
      
      if (labelResponse.success) {
        // Refetch fulfillment group data to get the new delivery records
        await refetchFulfillmentGroup();
        
        if (approvedReturnItems.length > 0) {
          const updatedItems = approvedReturnItems.map(item => ({
            ...item,
            status: 'return_label_generated'
          }));
          setApprovedReturnItems(updatedItems);
        }
        toast.success('Return shipping label generated successfully!');
      } else {
        throw new Error(labelResponse.message || 'Failed to generate return label');
      }
    } catch (error) {
      console.error('Error generating return label:', error);
      
      // Handle specific error types
      if (error.data && error.data.error === 'RATE_NO_LONGER_AVAILABLE') {
        // Rate is no longer available - show specific message and trigger rate refresh
        toast.error('The selected shipping rate is no longer available. Please select a new rate.');
        
        // Clear the selected rate and trigger a rate refresh
        setSelectedRate(null);
        if (selectedCustomerAddress && selectedMerchantAddress) {
          setShouldCalculateRates(true);
        }
        
        return; // Exit early to avoid setting isGeneratingLabel to false yet
      }
      
      if (error.message && (
        error.message.includes('customs declaration') || 
        error.message.includes('international shipment')
      )) {
        const customsError = 'This appears to be an international shipment which requires customs information. Please ensure both addresses have correct country codes.';
        toast.error(customsError);
      } else {
        toast.error(error.message || 'Failed to generate return shipping label');
      }
    } finally {
      setIsGeneratingLabel(false);
    }
  };

  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
    toast.error(errorMessage);
  };

  const handleCancelReturn = () => setShowCancelModal(true);
  
  const handleCancelComplete = (result) => {
    if (result && result.success) {
      toast.success(result.message || 'Return cancelled successfully');
      history.push('/dashboard/transactions');
    } else if (result) {
      toast.error(result.message || 'Failed to cancel return');
    }
    setShowCancelModal(false);
  };

  const isLoading = isGeneratingReturnLabel || isLoadingFG || isLoadingRates || isLoadingPaymentMethods;

  // Loading state
  if (isLoading && !error && !fgError) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Error state
  if (error || fgError) {
    return (
      <div className="p-6">
        <ErrorMessage message={error || fgError?.message || 'Failed to load return data'} />
        <button 
          onClick={() => setError(null)}
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-teal hover:bg-brand-teal-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-teal"
        >
          Try Again
        </button>
      </div>
    );
  }

  // No items found
  if (approvedReturnItems.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500 mb-4">No approved return items found.</p>
        <Button
          variant="secondary"
          onClick={() => history.push('/dashboard/transactions')}
        >
          Return to Transactions
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex items-center mb-6">
        <TruckIcon className="h-10 w-10 text-brand-teal mr-4" />
        <div>
          <h1 className="text-2xl font-medium text-gray-900">Return Shipping</h1>
          <p className="text-sm text-gray-600 mt-1">
            Generate return labels for {approvedReturnItems.length} item{approvedReturnItems.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <ReturnItemsList 
        approvedReturnItems={approvedReturnItems}
        groupReturnReason={groupReturnReason}
      />

      {approvedReturnItems.length > 0 && (
        <FinancialSummary 
          orderItems={approvedReturnItems} 
          shippingCost={shippingCost}
          currency={approvedReturnItems[0]?.currency || 'USD'} 
          isLoadingShipping={hasExistingShippingRate || labelsAlreadyCreated ? false : (isLoadingRates || !shouldCalculateRates)}
        />
      )}

        <ReturnShippingForm
          selectedCustomerAddress={selectedCustomerAddress}
          handleCustomerAddressChange={handleCustomerAddressChange}
          selectedMerchantAddress={selectedMerchantAddress}
          addressesLoading={addressesLoading}
          returnDetails={returnDetails}
          isLoadingRates={hasExistingShippingRate || labelsAlreadyCreated ? false : isLoadingRates}
          ratesError={error}
          rateData={{ rates: rawRates }}
          selectedRate={selectedRate}
          handleRateSelect={handleRateSelect}
          requiresPayment={requiresPayment}
          fulfillmentGroupId={fulfillmentGroupId}
          paymentAmount={paymentAmount > 0 ? paymentAmount : 0}
              onPaymentSuccess={handlePaymentSuccess}
              onPaymentError={handlePaymentError}
          savedPaymentMethods={paymentMethods}
              isGeneratingLabel={isGeneratingLabel}
          orderItemId={approvedReturnItems[0]?.id}
          isReturn={true}
        existingLabelData={existingLabelData}
        allHaveLabels={allHaveLabels}
      />

      <ReturnActionButtons
        allHaveLabels={allHaveLabels}
        isGeneratingLabel={isGeneratingLabel}
        isCancellingReturn={isCancellingReturn}
        onCancelReturn={handleCancelReturn}
        onBack={() => history.goBack()}
        onDone={() => history.push('/dashboard/transactions')}
      />
      
      {fgData && (
        <CancelGroupReturnModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          onConfirm={handleCancelComplete}
          entity={fgData}
        />
      )}
    </div>
  );
};

export default ReturnShippingPageWrapper; 